import { useCallback } from 'react';
import { Stack } from '@mantine/core';
import ProjectRole from '@projectMembers/models/projectRole';
import Project from 'src/entities/project';
import useRequestSeatAsLimitedEditorMutation from '@projects/queries/useRequestSeatAsLimitedEditorMutation';
import useProjectMembersList from './useProjectMembersList';
import ProjectMemberListItem from './ProjectMemberListItem/ProjectMemberListItem';
import ProjectInvitationListItem from './ProjectInvitationListItem/ProjectInvitationListItem';

export type ProjectMembersListProps = {
	project: Project;
	currentUserRole: ProjectRole;
	currentUserID: number;
};

const ProjectMembersList = ( { project, currentUserRole, currentUserID }: ProjectMembersListProps ) => {
	const {
		members,
		invitations,
		roleOptions,
		canEditProjectMember,
		canDeleteProjectMember,
		canDeleteProjectInvitation,
		onDeleteProjectMember,
		onUpdateProjectMemberRole,
		onDeleteProjectInvitation
	} = useProjectMembersList( { project, currentUserRole } );

	const { mutate: requestSeat } = useRequestSeatAsLimitedEditorMutation();
	const onRequestSeat = useCallback(
		() => requestSeat( { projectID: project.id } ),
		[ requestSeat, project.id ]
	);

	return (
		<Stack style={{ height: 'fit-content' }} spacing={0}>
			{members.map( member => (
				<ProjectMemberListItem
					key={member.id}
					member={member}
					currentUserRole={currentUserRole}
					roleOptions={roleOptions}
					workspaceID={project.workspaceID}
					isCurrentUser={currentUserID === member.user.id}
					readonly={!canEditProjectMember( member )}
					showDelete={canDeleteProjectMember( member )}
					onDelete={onDeleteProjectMember}
					onUpdateRole={onUpdateProjectMemberRole}
					onRequestSeat={onRequestSeat}
				/>
			) )}
			{invitations.map( invitation => (
				<ProjectInvitationListItem
					key={invitation.id}
					invitation={invitation}
					showDelete={canDeleteProjectInvitation( invitation )}
					onDelete={onDeleteProjectInvitation}
				/>
			) )}
		</Stack>
	);
};

export default ProjectMembersList;
